import { immer } from 'zustand/middleware/immer';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import type { PerpOrderType, PerpSide } from '@/lib/hyperliquid/types';

export const DEFAULT_PERPS_COIN = 'BTC';

export type PerpsOrderFormStore = {
  // order form
  selectedCoin: string;
  side: PerpSide;
  orderType: PerpOrderType;
  /** Limit price draft (limit / stop-limit) as typed; empty when unset. */
  limitPrice: string;
  /** Trigger price draft (take-profit / stop) as typed; empty when unset. */
  triggerPrice: string;
  /** Order size in USD as typed; empty when unset. */
  sizeUsd: string;
  /** Only reduce an open position; never open or flip one. */
  reduceOnly: boolean;
  /** Attach take-profit / stop-loss triggers to a market or limit order. */
  tpslEnabled: boolean;
  /** TP/SL trigger price drafts as typed; empty when unset. */
  takeProfitPrice: string;
  stopLossPrice: string;

  setSelectedCoin: (coin: string) => void;
  setSide: (side: PerpSide) => void;
  setOrderType: (orderType: PerpOrderType) => void;
  setLimitPrice: (limitPrice: string) => void;
  setTriggerPrice: (triggerPrice: string) => void;
  setSizeUsd: (sizeUsd: string) => void;
  setReduceOnly: (reduceOnly: boolean) => void;
  setTpslEnabled: (enabled: boolean) => void;
  setTpslPrice: (kind: 'takeProfit' | 'stopMarket', price: string) => void;
  /**
   * Book click-to-fill: adopt the price where the current order type uses it. Take-profit /
   * stop-market take it as the trigger; everything else as the limit, leaving market mode
   * so it is actually used.
   */
  pickBookPrice: (px: string) => void;
};

export const usePerpsOrderFormStore = createWithEqualityFn(
  immer<PerpsOrderFormStore>((set) => ({
    selectedCoin: DEFAULT_PERPS_COIN,
    side: 'long',
    orderType: 'market',
    limitPrice: '',
    triggerPrice: '',
    sizeUsd: '',
    reduceOnly: false,
    tpslEnabled: false,
    takeProfitPrice: '',
    stopLossPrice: '',

    setSelectedCoin: (selectedCoin) =>
      set({
        selectedCoin,
        limitPrice: '',
        triggerPrice: '',
        tpslEnabled: false,
        takeProfitPrice: '',
        stopLossPrice: '',
      }),
    setSide: (side) => set({ side }),
    // Prices mean different things per type (a short's TP sits below entry, its stop above),
    // so a stale value would flip the percentage input's direction.
    setOrderType: (orderType) =>
      set((state) =>
        state.orderType === orderType ? {} : { orderType, limitPrice: '', triggerPrice: '' },
      ),
    setLimitPrice: (limitPrice) => set({ limitPrice }),
    setTriggerPrice: (triggerPrice) => set({ triggerPrice }),
    setSizeUsd: (sizeUsd) => set({ sizeUsd }),
    // Reduce-only closes a position while TP/SL guard a new one, so they are mutually exclusive.
    setReduceOnly: (reduceOnly) => set({ reduceOnly, ...(reduceOnly && { tpslEnabled: false }) }),
    setTpslEnabled: (tpslEnabled) =>
      set({ tpslEnabled, ...(tpslEnabled && { reduceOnly: false }) }),
    setTpslPrice: (kind, price) =>
      set(kind === 'takeProfit' ? { takeProfitPrice: price } : { stopLossPrice: price }),
    pickBookPrice: (px) =>
      set((state) => {
        if (state.orderType === 'takeProfit' || state.orderType === 'stopMarket') {
          state.triggerPrice = px;
          return;
        }
        state.limitPrice = px;
        if (state.orderType === 'market') state.orderType = 'limit';
      }),
  })),
  shallow,
);

export const orderFormSelector = (state: PerpsOrderFormStore) => ({
  side: state.side,
  orderType: state.orderType,
  limitPrice: state.limitPrice,
  triggerPrice: state.triggerPrice,
  sizeUsd: state.sizeUsd,
  reduceOnly: state.reduceOnly,
  tpslEnabled: state.tpslEnabled,
  takeProfitPrice: state.takeProfitPrice,
  stopLossPrice: state.stopLossPrice,
  setSide: state.setSide,
  setOrderType: state.setOrderType,
  setLimitPrice: state.setLimitPrice,
  setTriggerPrice: state.setTriggerPrice,
  setSizeUsd: state.setSizeUsd,
  setReduceOnly: state.setReduceOnly,
});
